import React, { useState, useEffect, useRef } from 'react';
import './About.css';
import { motion, AnimatePresence } from 'framer-motion';
import useClickSound from '../../hooks/useClickSound';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Pagination, Autoplay, Navigation } from 'swiper/modules';
import { FaQuoteLeft, FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import 'swiper/css';
import 'swiper/css/effect-cards';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, TextPlugin);

const About = () => {
  const [activeTab, setActiveTab] = useState('professional_skills');
  const playClickSound = useClickSound();
  
  // Refs for GSAP animations
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const skillsRef = useRef(null);
  const brandRef = useRef(null);
  const testimonialsRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    // Header animation
    gsap.from(headerRef.current, {
      y: -50,
      opacity: 0,
      duration: 1,
      ease: "power3.out"
    });

    // Smooth scroll animation for timeline items
    const timelineItems = timelineRef.current?.querySelectorAll('.timeline-card');
    timelineItems?.forEach((item, index) => {
      gsap.from(item, {
        scrollTrigger: {
          trigger: item,
          start: "top bottom-=100",
          toggleActions: "play none none reverse"
        },
        opacity: 0,
        y: 50,
        duration: 0.8,
        delay: index * 0.2,
        ease: "power3.out"
      });
    });

    // Skills progress bars animation
    const progressBars = skillsRef.current?.querySelectorAll('.progress');
    progressBars?.forEach((bar) => {
      const width = bar.style.width;
      gsap.fromTo(bar, 
        { width: "0%" },
        {
          width: width,
          duration: 1.5,
          ease: "power2.out",
          scrollTrigger: {
            trigger: bar,
            start: "top bottom-=100",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    // Brand cards parallax effect
    const brandCards = brandRef.current?.querySelectorAll('.brand-card');
    brandCards?.forEach((card) => {
      gsap.to(card, {
        scrollTrigger: {
          trigger: card,
          start: "top bottom",
          end: "bottom top",
          scrub: 1
        },
        y: 50,
        ease: "none"
      });
    });

    // Stats counter animation
    const statNumbers = document.querySelectorAll('.stat-item h3');
    statNumbers.forEach((stat) => {
      gsap.from(stat, {
        textContent: 0,
        duration: 2,
        ease: "power2.out",
        snap: { textContent: 1 },
        stagger: {
          each: 0.2,
          onUpdate: function() {
            this.targets()[0].innerHTML = Math.ceil(this.targets()[0].textContent);
          },
        },
        scrollTrigger: {
          trigger: stat,
          start: "top bottom-=100",
        }
      });
    });
  }, [activeTab]);

  const handleTabClick = (tab) => {
    playClickSound();
    setActiveTab(tab);
    
    // Animate content switch
    gsap.to(".dashboard-content", {
      opacity: 0,
      y: -20,
      duration: 0.3,
      onComplete: () => {
        setActiveTab(tab);
        gsap.to(".dashboard-content", {
          opacity: 1,
          y: 0,
          duration: 0.3,
          delay: 0.1,
          onComplete: () => {
            // Scroll to content area
            const contentElement = document.querySelector(".dashboard-content");
            if (contentElement) {
              const yOffset = -100; // Offset to account for any fixed headers
              const y = contentElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
              window.scrollTo({
                top: y,
                behavior: 'smooth'
              });
            }
          }
        });
      }
    });
  };

  const tabVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <section id="about" className="about-section" ref={sectionRef}>
      <div className="container">
        <div ref={headerRef}>
        <p className="years-text">7+ YEARS OF EXPERIENCE</p>
        <motion.h2 
          className="section-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          My Resume
        </motion.h2>
        </div>

      {/* Tab Navigation */}
        <div className="dashboard-wrapper">
          <motion.div 
            className="dashboard-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {[
              {
                title: 'Professional Skills',
                description: 'Development & Design Skills',
                icon: '💻',
                preview: 'Full-Stack • UI/UX • Security',
                color: '#67aaf7'
              },
              {
                title: 'Experience',
                description: 'Professional Journey',
                icon: '🚀',
                preview: '7+ Years of Innovation',
                color: '#dda36c'
              },
              {
                title: 'Testimonials',
                description: 'Client Reviews',
                icon: '⭐',
                preview: 'Success Stories',
                color: '#bc8acf'
              },
              {
                title: 'Personal Branding',
                description: 'Brand Story',
                icon: '💎',
                preview: 'Vision & Values',
                color: '#ff4a57'
              }
            ].map((item) => (
              <motion.div
                key={item.title}
                className={`dashboard-card ${activeTab === item.title.toLowerCase().replace(' ', '_') ? 'active' : ''}`}
                onClick={() => handleTabClick(item.title.toLowerCase().replace(' ', '_'))}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.98 }}
                style={{
                  '--card-color': item.color
                }}
              >
                <div className="card-content">
                  <div className="card-icon">{item.icon}</div>
                  <div className="card-text">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                  <div className="card-preview">
                    <span>{item.preview}</span>
                    <motion.div 
                      className="preview-indicator"
                      initial={{ width: 0 }}
                      animate={{ width: activeTab === item.title.toLowerCase().replace(' ', '_') ? '100%' : '0%' }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>
                <motion.div 
                  className="card-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: activeTab === item.title.toLowerCase().replace(' ', '_') ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Content Section */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={tabVariants}
            transition={{ duration: 0.3 }}
              className="dashboard-content"
          >
            {activeTab === 'professional_skills' && (
              <motion.div 
                className="skills-container"
                  ref={skillsRef}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Development Skills Column */}
            <div className="skills-column">
                  <h3 className="features-text">FEATURES</h3>
                  <h2 className="skill-title">Development Skill</h2>
              <div className="skill-item">
                <div className="skill-info">
                      <span>REACT / NEXT.JS</span>
                  <span>100%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress" style={{ width: '100%' }}></div>
                </div>
              </div>

              <div className="skill-item">
                <div className="skill-info">
                      <span>JAVASCRIPT / TYPESCRIPT</span>
                  <span>95%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress" style={{ width: '95%' }}></div>
                </div>
              </div>

              <div className="skill-item">
                <div className="skill-info">
                      <span>NODE.JS / EXPRESS</span>
                      <span>90%</span>
                </div>
                <div className="progress-bar">
                      <div className="progress" style={{ width: '90%' }}></div>
                </div>
              </div>

              <div className="skill-item">
                <div className="skill-info">
                      <span>PYTHON / DJANGO</span>
                      <span>85%</span>
                </div>
                <div className="progress-bar">
                      <div className="progress" style={{ width: '85%' }}></div>
                </div>
              </div>

              <div className="skill-item">
                <div className="skill-info">
                      <span>AI / ML INTEGRATION</span>
                      <span>80%</span>
                </div>
                <div className="progress-bar">
                      <div className="progress" style={{ width: '80%' }}></div>
                </div>
              </div>
            </div>

                {/* Design Skills Column */}
            <div className="skills-column">
                  <h3 className="features-text">FEATURES</h3>
                  <h2 className="skill-title">Design Skill</h2>
              <div className="skill-item">
                <div className="skill-info">
                      <span>FIGMA</span>
                  <span>95%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress" style={{ width: '95%' }}></div>
                </div>
              </div>

              <div className="skill-item">
                <div className="skill-info">
                      <span>UI/UX DESIGN</span>
                      <span>90%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress" style={{ width: '90%' }}></div>
                    </div>
                  </div>

                  <div className="skill-item">
                    <div className="skill-info">
                      <span>ADOBE XD</span>
                      <span>85%</span>
                </div>
                <div className="progress-bar">
                      <div className="progress" style={{ width: '85%' }}></div>
                </div>
              </div>

              <div className="skill-item">
                <div className="skill-info">
                      <span>PHOTOSHOP</span>
                      <span>80%</span>
                </div>
                <div className="progress-bar">
                      <div className="progress" style={{ width: '80%' }}></div>
                </div>
              </div>

              <div className="skill-item">
                <div className="skill-info">
                      <span>ILLUSTRATOR</span>
                      <span>75%</span>
                </div>
                <div className="progress-bar">
                      <div className="progress" style={{ width: '75%' }}></div>
                </div>
              </div>
            </div>
              </motion.div>
        )}

        {activeTab === 'experience' && (
                <div className="timeline-container" ref={timelineRef}>
                <div className="timeline">
                  <motion.h3 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                      2011 - Present
                  </motion.h3>
                  <motion.h2
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                      Professional Journey
                  </motion.h2>
                  
                    {/* Current Positions */}
                  <motion.div 
                    className="timeline-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="timeline-card">
                        <div className="timeline-date">2024 - Present</div>
                        <h4>IT Head</h4>
                        <p className="company">Summit Visio</p>
                        <div className="location-tag">South Africa</div>
                        <p>Leading digital transformation initiatives and implementing cutting-edge AI solutions. Overseeing full-stack development projects and establishing robust IT infrastructure while ensuring security compliance and optimal performance.</p>
                      </div>
                    </motion.div>

                    <motion.div 
                      className="timeline-item"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="timeline-card">
                        <div className="timeline-date">2023 - Present</div>
                        <h4>IT Head</h4>
                        <p className="company">iGlam</p>
                        <div className="location-tag">South Africa</div>
                        <p>Spearheading technological innovation and digital strategy. Architecting and implementing secure, scalable solutions using Next.js, React, and AI integration. Leading development of enterprise-level applications with focus on UI/UX excellence.</p>
                    </div>
                  </motion.div>
                  
                    <motion.div 
                      className="timeline-item"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      <div className="timeline-card">
                        <div className="timeline-date">2021 - 2023</div>
                        <h4>IT Head</h4>
                        <p className="company">Digifinite</p>
                        <div className="location-tag">South Africa</div>
                        <p>Led comprehensive IT operations and strategic planning. Implemented automated workflows and modern development practices. Managed cross-functional teams and delivered high-impact digital solutions.</p>
                      </div>
                    </motion.div>
                </div>

                  {/* Previous Positions */}
            <div className="timeline">
                  <motion.h3
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                      2011 - 2020
                  </motion.h3>
                  <motion.h2
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                      Earlier Positions
                  </motion.h2>
                  
                  <motion.div 
                    className="timeline-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                <div className="timeline-card">
                        <div className="timeline-date">2019 - 2020</div>
                        <h4>IT Course Trainer</h4>
                        <p className="company">Bytes People Solution</p>
                        <div className="location-tag">South Africa</div>
                        <p>Delivered comprehensive IT training programs focusing on modern development practices, security, and enterprise solutions. Developed and implemented practical curriculum for aspiring IT professionals.</p>
                      </div>
                    </motion.div>

                    <motion.div 
                      className="timeline-item"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="timeline-card">
                        <div className="timeline-date">2019</div>
                        <h4>Certified Ethical Hacker Trainer</h4>
                        <p className="company">Torque IT</p>
                        <div className="location-tag">South Africa</div>
                        <p>Conducted specialized training in ethical hacking and cybersecurity. Equipped professionals with advanced security testing methodologies and best practices in information security.</p>
                </div>
                  </motion.div>
                  
                    <motion.div 
                      className="timeline-item"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      <div className="timeline-card">
                        <div className="timeline-date">2011 - 2018</div>
                        <h4>DBS IT Head</h4>
                        <p className="company">WMA Canaanland</p>
                        <div className="location-tag">Nigeria</div>
                        <p>Led and transformed IT operations for a major institution. Managed database systems, implemented security protocols, and established IT infrastructure. Pioneered digital initiatives and mentored IT team members.</p>
                      </div>
                    </motion.div>
                  </div>
              </div>
            )}

            {activeTab === 'testimonials' && (
                <motion.div 
                  className="testimonials-container"
                  ref={testimonialsRef}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <h3 className="features-text text-center">WHAT CLIENTS SAY</h3>
                  <h2 className="skill-title text-center mb-50">Testimonials</h2>
                  
                  <div className="swiper-container">
                    <div className="swiper-nav swiper-button-prev">
                      <FaChevronLeft />
                    </div>
                    <div className="swiper-nav swiper-button-next">
                      <FaChevronRight />
                    </div>
                    <Swiper
                      effect={'cards'}
                      grabCursor={true}
                      modules={[EffectCards, Pagination, Autoplay, Navigation]}
                      className="testimonials-swiper"
                      pagination={{
                        clickable: true,
                        dynamicBullets: true,
                      }}
                      navigation={{
                        prevEl: '.swiper-button-prev',
                        nextEl: '.swiper-button-next',
                      }}
                      autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                      }}
                      loop={true}
                    >
                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "Sam transformed our business with cutting-edge AI solutions. His expertise in security and full-stack development helped us achieve a 200% increase in efficiency. A true technology visionary who delivers beyond expectations."
                          </p>
                          <div className="testimonial-author">
                            <h4>James</h4>
                            <p>CTO, Innovation Hub SA</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "Working with Sam was a game-changer for our cybersecurity infrastructure. His ethical hacking expertise identified critical vulnerabilities and implemented robust solutions that saved us millions in potential breaches."
                          </p>
                          <div className="testimonial-author">
                            <h4>Shepherd</h4>
                            <p>Security Director, CyberShield SA</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "The UI/UX transformation Sam brought to our platform was remarkable. His deep understanding of user experience and modern design principles resulted in a 150% increase in user engagement and retention."
                          </p>
                          <div className="testimonial-author">
                            <h4>Nomvula</h4>
                            <p>Product Lead, Digital Solutions</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "Sam's expertise in AI integration revolutionized our data analytics platform. His innovative approach and technical prowess helped us achieve unprecedented accuracy in our predictive models."
                          </p>
                          <div className="testimonial-author">
                            <h4>Oluwaseun</h4>
                            <p>AI Research Lead, TechForward</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "As a startup founder, working with Sam was the best decision we made. His full-stack expertise and security-first approach helped us build a robust, scalable platform that our investors love."
                          </p>
                          <div className="testimonial-author">
                            <h4>Sarah</h4>
                            <p>CEO, TechStart Ventures</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "Sam's training programs in cybersecurity have become the gold standard in our industry. His practical approach and deep knowledge have helped countless professionals advance their careers."
                          </p>
                          <div className="testimonial-author">
                            <h4>Talent</h4>
                            <p>Director, CyberEd Solutions</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "The mobile-first approach and responsive design patterns Sam implemented have significantly improved our user experience. Our app now ranks among the top in the African market."
                          </p>
                          <div className="testimonial-author">
                            <h4>Chioma</h4>
                            <p>Mobile Lead, AppAfrica</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "Sam's implementation of our e-commerce platform exceeded all expectations. His attention to security details and seamless payment integration resulted in zero fraud cases since launch."
                          </p>
                          <div className="testimonial-author">
                            <h4>Emma</h4>
                            <p>E-commerce Director, SecureCart</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "The AI chatbot Sam developed for our customer service has reduced response times by 80%. His innovative solutions have significantly improved our customer satisfaction scores."
                          </p>
                          <div className="testimonial-author">
                            <h4>Themba</h4>
                            <p>Innovation Manager, AI Solutions</p>
                          </div>
                        </div>
                      </SwiperSlide>

                      <SwiperSlide>
                        <div className="testimonial-card">
                          <div className="quote-icon">
                            <FaQuoteLeft />
                          </div>
                          <div className="rating">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} />
                            ))}
                          </div>
                          <p className="testimonial-text">
                            "Sam's expertise in Next.js and React has transformed our web application's performance. His optimization techniques reduced our loading times by 70% and improved our SEO rankings dramatically."
                          </p>
                          <div className="testimonial-author">
                            <h4>Chinedu</h4>
                            <p>Technical Lead, WebTech SA</p>
                          </div>
                        </div>
                      </SwiperSlide>
                    </Swiper>
          </div>
                </motion.div>
        )}

            {activeTab === 'personal_branding' && (
                <motion.div 
                  className="personal-brand-container"
                  ref={brandRef}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {/* Brand Story Section */}
                  <div className="brand-story-section">
                    <h3 className="features-text text-center">MY BRAND STORY</h3>
                    <h2 className="skill-title text-center mb-50">Building Digital Excellence</h2>
                    
                    <motion.div 
                      className="brand-cards-container"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      {/* Vision Card */}
                      <motion.div 
                        className="brand-card"
                        whileHover={{ scale: 1.05, rotateY: 10 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <div className="card-icon">🎯</div>
                        <h3>Vision</h3>
                        <p>Transforming businesses through innovative technology solutions, focusing on security, efficiency, and user experience.</p>
                      </motion.div>

                      {/* Mission Card */}
                      <motion.div 
                        className="brand-card"
                        whileHover={{ scale: 1.05, rotateY: -10 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <div className="card-icon">🚀</div>
                        <h3>Mission</h3>
                        <p>Delivering cutting-edge solutions that bridge the gap between complex technology and seamless user experiences.</p>
                      </motion.div>

                      {/* Values Card */}
                      <motion.div 
                        className="brand-card"
                        whileHover={{ scale: 1.05, rotateY: 10 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <div className="card-icon">💎</div>
                        <h3>Values</h3>
                        <p>Innovation, Security, Excellence, and User-Centric Design drive every project and solution.</p>
                      </motion.div>
                    </motion.div>

                    {/* Statistics Section */}
                    <div className="statistics-section">
                      <motion.div 
                        className="stats-container"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <div className="stat-item">
                          <h3>7+</h3>
                          <p>Years Experience</p>
                        </div>
                        <div className="stat-item">
                          <h3>50+</h3>
                          <p>Projects Completed</p>
                        </div>
                        <div className="stat-item">
                          <h3>100%</h3>
                          <p>Client Satisfaction</p>
                        </div>
                        <div className="stat-item">
                          <h3>24/7</h3>
                          <p>Support Available</p>
                        </div>
                      </motion.div>
                    </div>

                    {/* Expertise Showcase */}
                    <div className="expertise-section">
                      <h3 className="features-text text-center">CORE EXPERTISE</h3>
                      <div className="expertise-grid">
                        <motion.div 
                          className="expertise-card"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <h4>Full-Stack Development</h4>
                          <p>Next.js • React • Node.js • Python</p>
                          <motion.div 
                            className="expertise-progress"
                            initial={{ width: 0 }}
                            animate={{ width: "95%" }}
                            transition={{ duration: 1 }}
                          />
                        </motion.div>

                        <motion.div 
                          className="expertise-card"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <h4>Cybersecurity</h4>
                          <p>Ethical Hacking • Security Audits • Training</p>
                          <motion.div 
                            className="expertise-progress"
                            initial={{ width: 0 }}
                            animate={{ width: "90%" }}
                            transition={{ duration: 1 }}
                          />
                        </motion.div>

                        <motion.div 
                          className="expertise-card"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <h4>AI Integration</h4>
                          <p>Machine Learning • ChatGPT • Custom AI Solutions</p>
                          <motion.div 
                            className="expertise-progress"
                            initial={{ width: 0 }}
                            animate={{ width: "85%" }}
                            transition={{ duration: 1 }}
                          />
                        </motion.div>
                      </div>
                    </div>

                    {/* Call-to-Action Section */}
                    <motion.div 
                      className="cta-section"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="cta-container">
                        <div className="cta-content">
                          <h2>Ready to Transform Your Digital Presence?</h2>
                          <p>Let's collaborate to bring your vision to life with cutting-edge technology and security-first solutions.</p>
                          <div className="cta-buttons">
                            <motion.button 
                              className="cta-button primary"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => window.location.href = '#contact'}
                            >
                              Start a Project
                            </motion.button>
                            <motion.button 
                              className="cta-button secondary"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => window.location.href = '/portfolio'}
                            >
                              View Portfolio
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
          </div>
                </motion.div>
        )}
          </motion.div>
        </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default About;
